import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { AdvancedSelect, Decorator, Input } from "@myhealth-raccoon/components";
import { validation } from "@myhealth-raccoon/forms";
import { FundCode, FundCodes } from "src/utils/fundCodes";
import classNames from "classnames";


export type UpdatePHIFormValues = {
    havePhi: "yes" | "no";
    providerId: string | number;
    providerName: string;
    customerId: string;
    expiry: string;
};

type Props = {
    className?: string;
    onCancel?(): void;
    onSubmit?(values: UpdatePHIFormValues): unknown;
    defaultValues?: Partial<UpdatePHIFormValues>;
    canRemovePHI?: boolean;
    canCancel?: boolean;
    setIsDirty?(isDirty: boolean): void;
    required?: boolean;
}

export interface PHIFormRef {
    submit(): Promise<UpdatePHIFormValues>;
}

export const UpdatePHIForm = forwardRef<PHIFormRef, Props>(({ className, onCancel, onSubmit, defaultValues, canRemovePHI, canCancel, setIsDirty, required }, ref) => {
    const {
        handleSubmit,
        register,
        formState: { errors, isSubmitting, isDirty },
        watch,
        setValue
    } = useForm<UpdatePHIFormValues>({ defaultValues });

    useEffect(() => {
        setIsDirty?.(watch("havePhi") === "yes" && isDirty)
    }, [isDirty, setIsDirty, watch]);

    const doCancel = () => {
        if (canRemovePHI) {
            setValue('havePhi', 'no');
        }
        if (onCancel) { onCancel(); }
    }

    useImperativeHandle(ref, () => ({
        submit: async () => {
            return new Promise((resolve, reject) => {
                handleSubmit(resolve, reject)()
            })
        }
    }));

    register("providerId", { validate: watch("havePhi") === "yes" ? validation.required() : undefined });
    // havePhi is only present when canRemovePHI === true
    const havePhi = register("havePhi", { validate: canRemovePHI && required ? validation.required() : undefined });

    return <form onSubmit={onSubmit && handleSubmit(onSubmit)} className={classNames("group", className)}>
        {canRemovePHI &&
            <Decorator fieldset label="Do you have Private Health Insurance?" requiredIndicator={required} fieldError={errors.havePhi}>
                <div className="flex flex-row gap-5 text-base py-1.5">
                    <Decorator
                        className="flex flex-row gap-3">
                        <input
                            type="radio"
                            className="radio align-bottom text-xl mt-0.5"
                            value="yes" {...havePhi} id="havePhiTrue"
                        />
                        <label htmlFor="havePhiTrue" className="text-gray-700 cursor-pointer">Yes</label>
                    </Decorator>
                    <Decorator
                        className="flex flex-row gap-3">
                        <input
                            type="radio"
                            className="radio align-bottom text-xl mt-0.5"
                            value="no" {...havePhi} id="havePhiFalse"
                        />
                        <label htmlFor="havePhiFalse" className="text-gray-700 cursor-pointer">No</label>
                    </Decorator>
                </div>
            </Decorator>
        }
        {watch("havePhi") === "yes" && <div className={classNames("mt-5 rounded-xl py-5 px-4 gap-5 flex flex-col bg-gray-50 border ", {
            "border-warning-300 group-focus-within:border-gray-200": isDirty && !isSubmitting,
            "border-gray-200": !isDirty || isSubmitting
        })}>
            <Decorator
                label="Who is your private health insurer?"
                fieldError={errors.providerId}
            >
                <AdvancedSelect<FundCode, false>
                    name="providerId"
                    options={FundCodes}
                    error={!!errors.providerId}
                    value={FundCodes.find(f => `${f.FundID}` === `${watch('providerId')}`)}
                    getOptionLabel={o => o.FundName}
                    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                    onChange={o => setValue('providerId', o?.FundID!)}
                    isOptionSelected={(o) => `${o.FundID}` === `${watch('providerId')}`}
                    blurInputOnSelect
                />
            </Decorator>
            {watch('providerId') === 9999 &&
                <Input
                    {...register("providerName", {
                        validate: validation.composeValidators(validation.required(), validation.regex(/^[A-Za-z\s\d-_]*$/, 'Please use only letters, number & hyphens.'))
                    })}
                    fieldError={errors.providerName}
                    placeholder="Please enter the name of your health insurer"
                />
            }

            <div className="flex flex-row gap-4">
                <Input
                    {...register("customerId", {
                        validate: validation.composeValidators(validation.required(), validation.regex(/^[A-Za-z\s\d-_]*$/, 'Please use only letters, number & hyphens.'))
                    })}
                    id="customerId"
                    fieldError={errors.customerId}
                    label="Health Insurance no."
                    className="flex-1"
                />
                <Input
                    {...register("expiry")}
                    id="expiry"
                    label="Expiry"
                    type="month"
                    className="flex-1"
                    fieldError={errors.expiry}
                />
            </div>

            <div className='flex flex-row gap-3 w-fit ml-auto'>
                {onCancel && canCancel && <button
                    type="button"
                    onClick={doCancel}
                    className='font-semibold'
                >Cancel</button>}
                {onSubmit && <button
                    disabled={isSubmitting}
                    type="submit"
                    className='font-semibold text-primary-700 disabled:text-gray-600'
                >Update</button>}
            </div>
        </div>}
        {onSubmit && isDirty && <p className="mt-2 text-warning group-focus-within:hidden">Please finish updating your Private Health Insurance</p>}
    </form>
});